/**
 * Order-success business case — one inline card after they place an order.
 * Never a modal. Never on checkout. Express picks the case and cool-offs.
 */

import { normalizeGrowthEnginePath } from './growthEngineNudgeRefresh'
import { isInternalOrFixtureTitle } from './growthEngineInternalTitle'

export const GROWTH_ENGINE_ORDER_SUCCESS_SURFACE = 'order_success'

/**
 * @param {string|null|undefined} pathname
 */
export function isGrowthEngineOrderSuccessPath(pathname) {
  return /\/checkout\/success$/.test(normalizeGrowthEnginePath(pathname))
}

/**
 * Any checkout route that is not the success screen — do not pitch here.
 * Covers `/checkout`, nested checkout steps, and subscriptions checkout.
 * @param {string|null|undefined} pathname
 */
export function isGrowthEngineCheckoutPath(pathname) {
  const path = normalizeGrowthEnginePath(pathname)
  if (!path || isGrowthEngineOrderSuccessPath(path)) return false
  return /\/checkout(?:\/|$)/.test(path)
}

/**
 * Never mount a business-case card while they are placing an order.
 * Success and venue chrome stay allowed.
 * @param {string|null|undefined} pathname
 */
export function shouldBlockGrowthEngineCaseWhileOrdering(pathname) {
  return isGrowthEngineCheckoutPath(pathname)
}

/** Hide the Home primary card on checkout and success so this surface is one card. */
export function shouldHidePrimaryNudgeOnCheckoutFlow(pathname) {
  return (
    isGrowthEngineCheckoutPath(pathname) ||
    isGrowthEngineOrderSuccessPath(pathname)
  )
}

/**
 * First product from the order they just placed (copy + CTA handoff).
 * Express fills last-ordered if this is missing.
 *
 * @param {object|null|undefined} orderData
 * @returns {{ title: string, productId: string|null }|null}
 */
export function pickOrderSuccessProduct(orderData) {
  const suppliers = Array.isArray(orderData?.suppliers)
    ? orderData.suppliers
    : []
  for (const supplier of suppliers) {
    const items = Array.isArray(supplier?.items) ? supplier.items : []
    for (const item of items) {
      const title = String(item?.productTitle || item?.title || '').trim()
      if (!title || isInternalOrFixtureTitle(title)) continue
      const rawId =
        item?.productId ||
        (typeof item?.product === 'string' ? item.product : item?.product?._id)
      const productId = rawId != null ? String(rawId).trim() : ''
      return { title, productId: productId || null }
    }
  }
  return null
}

/** @param {object|null|undefined} orderData */
export function pickOrderSuccessProductName(orderData) {
  return pickOrderSuccessProduct(orderData)?.title || null
}

/**
 * One show, then quiet — no remount replay on order success.
 *
 * @param {object|null|undefined} result
 * @param {{ shownIntentIds?: Set<string>|null }} [options]
 */
export function applyOrderSuccessRecommendation(
  result,
  { shownIntentIds = null } = {},
) {
  const next = result?.recommendation || null
  if (!next?.intentId) return null
  if (shownIntentIds && shownIntentIds.has(next.intentId)) return null
  return next
}

/**
 * Light the Purchasing-nav Tip when they leave order success with a case
 * still waiting. Opening the CTA, snooze, or dismiss must not mark it.
 *
 * @param {{
 *   hasWaitingCase?: boolean,
 *   openedCase?: boolean,
 *   putAway?: boolean,
 * }} [args]
 */
export function shouldMarkHomeTipAfterLeavingOrderSuccess({
  hasWaitingCase = false,
  openedCase = false,
  putAway = false,
} = {}) {
  return Boolean(hasWaitingCase && !openedCase && !putAway)
}
